import { useEffect, useRef, useState } from "react";
import {
  RequestApproveUserRequest,
  RequestApproveUserRequestWrapper,
  RequestDeleteUserRequest,
  RequestDeleteUserRequestWrapper,
  RequestGetUserListRequest,
  RequestUpdateUserRequest,
  ResponseGetUserListResonse,
} from "../../services/OpenAPI";
import { loginUserSelector } from "../../recoil/selectors/loginUserSelector";
import { Permission } from "../../constants/roles";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { useApiGetUserList } from "../api/useApiGetUserList";
import { UserListItem } from "../../components/Table/UserListTable";
import { useApiUpdateUser } from "../api/useApiUpdateUser";
import { userUpdatedFlagState } from "../../recoil/atoms/updateUserAtoms";
import { useApiApproveUser } from "../api/useApiApproveUser";
import { useApiDeleteUser } from "../api/useApiDeleteUser";
import { useApiGetWarehouses } from "../api/useApiGetWarehouses";
import { warehousesState } from "../../recoil/atoms/warehousesAtoms";

export type SearchGenreOption = {
  id: string;
  label: string;
};

export const useManagementUsers = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { fetchUserList } = useApiGetUserList();
  const { updateUser } = useApiUpdateUser();
  const { approveUsers } = useApiApproveUser();
  const { deleteUsers } = useApiDeleteUser();
  const loginUser = useRecoilValue(loginUserSelector);
  const setUserUpdatedFlag = useSetRecoilState(userUpdatedFlagState);
  const userUpdatedFlag = useRecoilValue(userUpdatedFlagState);
  const reserUserUpdatedFlag = useResetRecoilState(userUpdatedFlagState);
  const setWarehousesState = useSetRecoilState(warehousesState);

  const { fetchWarehouses } = useApiGetWarehouses();

  const [userList, setUserList] = useState<UserListItem[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<UserListItem | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);

  const handleCheckChange = (id: string) => {
    setCheckedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleCheckChangeAll = (newCheckedIds: Set<string>) => {
    setCheckedIds(newCheckedIds);
  };

  const transformData = (
    userList: ResponseGetUserListResonse[] | undefined
  ): UserListItem[] => {
    if (!Array.isArray(userList)) return [];
    const result: UserListItem[] = [];

    userList.forEach((user) => {
      const baseIds =
        user.baseInfo?.map((b) => b.id).filter((id): id is number => !!id) ??
        [];
      const baseNames =
        user.baseInfo
          ?.map((b) => b.name)
          .filter((name): name is string => !!name) ?? [];

      result.push({
        id: Number(user.id),
        userNumber: user.userNumber,
        lastName: user.lastName,
        firstName: user.firstName,
        mailAddress: user.mail,
        base: baseNames.join(", "),
        baseIds: baseIds,
        shopName: user.shopName,
        roomId: user.chatworkRoomId,
        driverName: user.driverName,
        approval: user.approval,
        memo: user.introducer,
        isHopeWarehouse: user.isHopeWarehouse,
        isHopeFsz1: user.isHopeFsz1,
        isHopeHnd2: user.isHopeHnd2,
        isHopeHnd3: user.isHopeHnd3,
        isHopeHnd6: user.isHopeHnd6,
        isHopeHnd9: user.isHopeHnd9,
        isHopeHsg1: user.isHopeHsg1,
        isHopeKix1: user.isHopeKix1,
        isHopeKix2: user.isHopeKix2,
        isHopeKix3: user.isHopeKix3,
        isHopeKix4: user.isHopeKix4,
        isHopeKix5: user.isHopeKix5,
        isHopeKix6: user.isHopeKix6,
        isHopeNrt1: user.isHopeNrt1,
        isHopeNrt2: user.isHopeNrt2,
        isHopeNrt5: user.isHopeNrt5,
        isHopeNgo2: user.isHopeNgo2,
        isHopeQcb1: user.isHopeQcb1,
        isHopeQcb3: user.isHopeQcb3,
        isHopeQcb4: user.isHopeQcb4,
        isHopeQcb5: user.isHopeQcb5,
        isHopeTpb1: user.isHopeTpb1,
        isHopeTpb2: user.isHopeTpb2,
        isHopeTpb3: user.isHopeTpb3,
        isHopeTpb5: user.isHopeTpb5,
        isHopeTpb9: user.isHopeTpb9,
        isHopeTpf2: user.isHopeTpf2,
        isHopeTpf3: user.isHopeTpf3,
        isHopeTpf4: user.isHopeTpf4,
        isHopeTpf6: user.isHopeTpf6,
        isHopeTpf9: user.isHopeTpf9,
        isHopeTpf_a: user.isHopeTpfA,
        isHopeTpf_b: user.isHopeTpfB,
        isHopeTpf_c: user.isHopeTpfC,
        isHopeTpf_d: user.isHopeTpfD,
        isHopeTpf_i: user.isHopeTpfI,
        isHopeTpn1: user.isHopeTpn1,
        isHopeTpn2: user.isHopeTpn2,
        isHopeTpn3: user.isHopeTpn3,
        isHopeTpx1: user.isHopeTpx1,
        isHopeTpx2: user.isHopeTpx2,
        isHopeTpy1: user.isHopeTpy1,
        isHopeTpy2: user.isHopeTpy2,
        isHopeTpy3: user.isHopeTpy3,
        isHopeTpy5: user.isHopeTpy5,
        isHopeTpz1: user.isHopeTpz1,
        isHopeTpz2: user.isHopeTpz2,
        isHopeTpz3: user.isHopeTpz3,
        isHopeTpz4: user.isHopeTpz4,
        isHopeTpz5: user.isHopeTpz5,
        isHopeTpz6: user.isHopeTpz6,
        isHopeTpz7: user.isHopeTpz7,
        isHopeTpz8: user.isHopeTpz8,
        isHopeTpz9: user.isHopeTpz9,
        isHopeTpr1: user.isHopeTpr1,
        isHopeTyo1: user.isHopeTyo1,
        isHopeTyo2: user.isHopeTyo2,
        isHopeTyo4: user.isHopeTyo4,
        isHopeTyo6: user.isHopeTyo6,
        isHopeTyo7: user.isHopeTyo7,
        isHopeTyo8: user.isHopeTyo8,
        isHopeTyo9: user.isHopeTyo9,
        isHopeVjg_t: user.isHopeVjgT,
        isHopeVjn_a: user.isHopeVjnA,
        isHopeVjn_b: user.isHopeVjnB,
        isHopeVjn_c: user.isHopeVjnC,
        isHopeVjn_d: user.isHopeVjnD,
        isHopeVjn_e: user.isHopeVjnE,
        isHopeVjw_a: user.isHopeVjwA,
        isHopeVjw_b: user.isHopeVjwB,
        isHopeXje1: user.isHopeXje1,
        isHopeXje2: user.isHopeXje2,
      });
    });

    return result;
  };

  const handleUpdate = async (updatedUser: UserListItem, baseIds: number[]) => {
    if (
      !loginUser ||
      !loginUser.tenantId ||
      !loginUser.id ||
      !loginUser.permissionId
    )
      return;
    const request: RequestUpdateUserRequest = {
      permissionId: loginUser.permissionId,
      tenantId: loginUser.tenantId,
      id: Number(updatedUser.id),
      userNumber: Number(updatedUser.userNumber),
      lastName: updatedUser.lastName,
      firstName: updatedUser.firstName,
      mail: updatedUser.mailAddress,
      baseId: Number(updatedUser.baseId),
      shopName: updatedUser.shopName,
      chatworkRoomId: Number(updatedUser.roomId),
      driverName: updatedUser.driverName,
      introducer: updatedUser.memo,
      baseIds: baseIds,
      isHopeWarehouse: updatedUser.isHopeWarehouse,
      isHopeFsz1: updatedUser.isHopeFsz1,
      isHopeHnd2: updatedUser.isHopeHnd2,
      isHopeHnd3: updatedUser.isHopeHnd3,
      isHopeHnd6: updatedUser.isHopeHnd6,
      isHopeHnd9: updatedUser.isHopeHnd9,
      isHopeHsg1: updatedUser.isHopeHsg1,
      isHopeKix1: updatedUser.isHopeKix1,
      isHopeKix2: updatedUser.isHopeKix2,
      isHopeKix3: updatedUser.isHopeKix3,
      isHopeKix4: updatedUser.isHopeKix4,
      isHopeKix5: updatedUser.isHopeKix5,
      isHopeKix6: updatedUser.isHopeKix6,
      isHopeNrt1: updatedUser.isHopeNrt1,
      isHopeNrt2: updatedUser.isHopeNrt2,
      isHopeNrt5: updatedUser.isHopeNrt5,
      isHopeNgo2: updatedUser.isHopeNgo2,
      isHopeQcb1: updatedUser.isHopeQcb1,
      isHopeQcb3: updatedUser.isHopeQcb3,
      isHopeQcb4: updatedUser.isHopeQcb4,
      isHopeQcb5: updatedUser.isHopeQcb5,
      isHopeTpb1: updatedUser.isHopeTpb1,
      isHopeTpb2: updatedUser.isHopeTpb2,
      isHopeTpb3: updatedUser.isHopeTpb3,
      isHopeTpb5: updatedUser.isHopeTpb5,
      isHopeTpb9: updatedUser.isHopeTpb9,
      isHopeTpf2: updatedUser.isHopeTpf2,
      isHopeTpf3: updatedUser.isHopeTpf3,
      isHopeTpf4: updatedUser.isHopeTpf4,
      isHopeTpf6: updatedUser.isHopeTpf6,
      isHopeTpf9: updatedUser.isHopeTpf9,
      isHopeTpfA: updatedUser.isHopeTpf_a,
      isHopeTpfB: updatedUser.isHopeTpf_b,
      isHopeTpfC: updatedUser.isHopeTpf_c,
      isHopeTpfD: updatedUser.isHopeTpf_d,
      isHopeTpfI: updatedUser.isHopeTpf_i,
      isHopeTpn1: updatedUser.isHopeTpn1,
      isHopeTpn2: updatedUser.isHopeTpn2,
      isHopeTpn3: updatedUser.isHopeTpn3,
      isHopeTpx1: updatedUser.isHopeTpx1,
      isHopeTpx2: updatedUser.isHopeTpx2,
      isHopeTpy1: updatedUser.isHopeTpy1,
      isHopeTpy2: updatedUser.isHopeTpy2,
      isHopeTpy3: updatedUser.isHopeTpy3,
      isHopeTpy5: updatedUser.isHopeTpy5,
      isHopeTpz1: updatedUser.isHopeTpz1,
      isHopeTpz2: updatedUser.isHopeTpz2,
      isHopeTpz3: updatedUser.isHopeTpz3,
      isHopeTpz4: updatedUser.isHopeTpz4,
      isHopeTpz5: updatedUser.isHopeTpz5,
      isHopeTpz6: updatedUser.isHopeTpz6,
      isHopeTpz7: updatedUser.isHopeTpz7,
      isHopeTpz8: updatedUser.isHopeTpz8,
      isHopeTpz9: updatedUser.isHopeTpz9,
      isHopeTpr1: updatedUser.isHopeTpr1,
      isHopeTyo1: updatedUser.isHopeTyo1,
      isHopeTyo2: updatedUser.isHopeTyo2,
      isHopeTyo4: updatedUser.isHopeTyo4,
      isHopeTyo6: updatedUser.isHopeTyo6,
      isHopeTyo7: updatedUser.isHopeTyo7,
      isHopeTyo8: updatedUser.isHopeTyo8,
      isHopeTyo9: updatedUser.isHopeTyo9,
      isHopeVjgT: updatedUser.isHopeVjg_t,
      isHopeVjnA: updatedUser.isHopeVjn_a,
      isHopeVjnB: updatedUser.isHopeVjn_b,
      isHopeVjnC: updatedUser.isHopeVjn_c,
      isHopeVjnD: updatedUser.isHopeVjn_d,
      isHopeVjnE: updatedUser.isHopeVjn_e,
      isHopeVjwA: updatedUser.isHopeVjw_a,
      isHopeVjwB: updatedUser.isHopeVjw_b,
      isHopeXje1: updatedUser.isHopeXje1,
      isHopeXje2: updatedUser.isHopeXje2,
    };
    const result = await updateUser(request);
    if (result) {
      setToastMessage("更新が完了しました");
      setToastSeverity("success");
      setToastOpen(true);
      setUserUpdatedFlag(true);
      fetchWarehouses().then((warehouses) => setWarehousesState(warehouses));
    } else {
      setToastMessage("更新に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleRowClick = async (user: UserListItem) => {
    setSelectedRow(user);
    setDetailDialogOpen(true);
  };

  const handleApprove = async () => {
    if (checkedIds.size === 0) return;

    // 選択されたユーザー情報をまとめる
    const selectedUsers = userList.filter((user) =>
      checkedIds.has(String(user.id))
    );

    // APIのリクエスト形式にマッピング
    const requestPayload: RequestApproveUserRequest[] = selectedUsers.map(
      (user) => ({
        id: Number(user.id),
        tenantId: loginUser?.tenantId,
        updatedUserId: loginUser?.id,
        baseId: user.baseId,
      })
    );
    const requestPayloadWrapper: RequestApproveUserRequestWrapper = {
      req: requestPayload,
    };

    const result = await approveUsers(requestPayloadWrapper);
    if (result) {
      setCheckedIds(new Set());
      setToastMessage("承認が完了しました");
      setToastSeverity("success");
      setToastOpen(true);
      setUserUpdatedFlag(true);
    } else {
      setToastMessage("承認に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleCancel = () => {
    setCheckedIds(new Set());
  };

  const handleDelete = async () => {
    if (checkedIds.size === 0) return;

    // 選択されたユーザー情報をまとめる
    const selectedUsers = userList.filter((user) =>
      checkedIds.has(String(user.id))
    );

    // APIのリクエスト形式にマッピング
    const requestPayload: RequestDeleteUserRequest[] = selectedUsers.map(
      (user) => ({
        id: Number(user.id),
        tenantId: loginUser?.tenantId,
        updatedUserId: loginUser?.id,
        baseId: user.baseId,
      })
    );

    const requestPayloadWrapper: RequestDeleteUserRequestWrapper = {
      req: requestPayload,
    };

    const result = await deleteUsers(requestPayloadWrapper);
    if (result) {
      setToastMessage("ユーザー削除に成功しました");
      setToastSeverity("success");
      setToastOpen(true);
      setCheckedIds(new Set());
      setUserUpdatedFlag(true);
    } else {
      setToastMessage("ユーザー削除に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const fetchData = async (req: RequestGetUserListRequest) => {
    const response = await fetchUserList(req);
    if (response) {
      const transformed = transformData(response);
      setUserList(transformed);
    }
  };

  useEffect(() => {
    if (userUpdatedFlag) {
      if (loginUser?.tenantId && loginUser?.id) {
        const req: RequestGetUserListRequest = {
          tenantId: loginUser.tenantId,
          baseId: loginUser.baseId!,
          permissionId: Permission.General,
          userId: loginUser.id,
        };
        fetchData(req);
      }
      reserUserUpdatedFlag();
      setDetailDialogOpen(false);
    }
  }, [userUpdatedFlag]);

  useEffect(() => {
    if (loginUser?.tenantId && loginUser?.id) {
      const req: RequestGetUserListRequest = {
        tenantId: loginUser.tenantId,
        baseId: loginUser.baseId!,
        permissionId: Permission.General,
        userId: loginUser.id,
      };
      fetchData(req);
    }
  }, [loginUser]);

  useEffect(() => {
    if (loginUser?.tenantId && loginUser?.id) {
      const req: RequestGetUserListRequest = {
        tenantId: loginUser.tenantId,
        baseId: loginUser.baseId!,
        permissionId: Permission.General,
        userId: loginUser.id,
      };
      fetchData(req);
    }
  }, []);

  return {
    inputRef,
    userList,
    selectedRow,
    checkedIds,
    handleCheckChange,
    handleUpdate,
    handleRowClick,
    detailDialogOpen,
    setDetailDialogOpen,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    handleApprove,
    handleCancel,
    handleDelete,
    handleCheckChangeAll,
  };
};
